import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'

import type { CartItem } from '~/api/cart/types'

import {
  isBillingAddressComplete,
  isCollectionPointAddressComplete,
  isShippingAddressComplete,
} from './address'
import { isBouyguesOffer } from './isBouyguesOffer'

export function scrollToFirstItemWithCompliancyError(
  breakpointValue: Breakpoint,
  listingId: string,
) {
  const insuranceCompliancyElement = document.getElementById(
    `compliancy-${listingId}`,
  )
  const insuranceElement = document.getElementById(`insurances-${listingId}`)

  if (breakpointValue >= Breakpoint.MD) {
    insuranceElement?.scrollIntoView({ block: 'start', behavior: 'smooth' })

    return
  }
  insuranceCompliancyElement?.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  })
}

export function getAvailableItems(items: CartItem[]): CartItem[] {
  return items.filter((item) => item.available === 'yes')
}

export function hasAvailableItems(items: CartItem[]): boolean {
  return getAvailableItems(items).length > 0
}

export function hasOutOfStockItem(items: CartItem[]): boolean {
  return items.some((item) => item.available === 'no')
}

/**
 * Determines if an order can be shipped based on address completeness and shipping availability.
 *
 * Validates two shipping scenarios:
 * - **Collection Point**: Requires complete collection point details + billing address
 * - **Regular Shipping**: Requires complete shipping address + billing address + shippable country
 *
 * @param addressList - Customer addresses (shipping, billing, optional collection point)
 * @param shippableCountries - List of countries where we can ship
 * @returns `true` if the order can be shipped, `false` otherwise
 */
export function isShippable(
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList'],
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries'],
): boolean {
  const hasCollectionPoint = Boolean(addressList.deliverCollectionPoint)

  const hasCompleteCollectionPointAddress = isCollectionPointAddressComplete(
    addressList.deliverCollectionPoint,
  )

  const hasCompleteShippingAddress = isShippingAddressComplete(
    addressList.deliver,
  )

  const hasCompleteBillingAddress = isBillingAddressComplete(addressList.bill)

  const isCountryShippable = shippableCountries.some(
    ({ countryCode }) => countryCode === addressList.deliver?.country,
  )

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const isShippable = hasCollectionPoint
    ? hasCompleteCollectionPointAddress
    : hasCompleteShippingAddress && isCountryShippable

  return isShippable && hasCompleteBillingAddress
}

function hasBouyguesMobilePlanOffer(item: CartItem) {
  if (!item.mobilePlan) return false

  return isBouyguesOffer(item.mobilePlan.selectedOffer)
}

export function getBouyguesMobilePlanOffer(cartItems: CartItem[]) {
  return cartItems.find(hasBouyguesMobilePlanOffer)?.mobilePlan?.selectedOffer
}
